import { useState } from 'react';
import { useTranslations } from 'use-intl';
import type { ReactionKind } from '@leanin/shared/reactions';
import {
  REACTIONS,
  REACTION_BY_ID,
  REACTION_LABEL_KEY,
  ReactionDisc,
} from '../posts/index';
import { PeopleListModal, type PeopleListTab } from '../people/index';
import { useFeedReactions, type ReactionTarget } from '../../lib/queries/feed';

type Props = {
  /** Post, comment, or message whose reactor roster the modal lists. */
  target: ReactionTarget;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Live per-kind totals from the host card — drives the tab badges without
   *  waiting on (or re-fetching) the paginated list. */
  counts: Record<ReactionKind, number>;
};

// LinkedIn-style "who reacted" sheet: tab per reaction kind that has any
// reactions (plus All), each row a member linking to their profile with the
// reaction they left.
export function ReactionsModal({ target, open, onOpenChange, counts }: Props) {
  const t = useTranslations('Feed');
  const tCommon = useTranslations('Common');
  const [tab, setTab] = useState<'all' | ReactionKind>('all');
  const kind = tab === 'all' ? undefined : tab;
  const query = useFeedReactions(target, {
    ...(kind !== undefined ? { kind } : {}),
    enabled: open,
  });

  const total =
    counts.support + counts.insightful + counts.same + counts.helpful;
  const tabs: PeopleListTab[] = [
    { id: 'all', label: t('reactions.all'), count: total },
    ...REACTIONS.filter((r) => counts[r.id] > 0).map((r) => ({
      id: r.id,
      label: t(REACTION_LABEL_KEY[r.id]),
      count: counts[r.id],
    })),
  ];

  return (
    <PeopleListModal
      open={open}
      onOpenChange={onOpenChange}
      title={t('reactions.modalTitle')}
      tabs={tabs}
      activeTab={tab}
      onTabChange={(id) => setTab(id as 'all' | ReactionKind)}
      query={query}
      toPerson={(r) => ({
        key: `${r.userId}:${r.kind}`,
        userId: r.userId,
        name: r.name,
        photoUrl: r.photoUrl,
        sublabel: [r.jobTitle, r.company].filter(Boolean).join(' · ') || null,
        right: (
          <ReactionDisc
            reaction={REACTION_BY_ID[r.kind as ReactionKind]}
            size={22}
          />
        ),
      })}
      loadingLabel={t('reactions.loadingLabel')}
      emptyLabel={tCommon('status.empty')}
    />
  );
}
